import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorUserMessage, readApiError } from "_shared/_errors/api-error";
import { getApiErrorData, getApiErrorStatus } from "_shared/_errors/api-error-message";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import Guid from "_shared/guids/guid";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import { EntityAgreementJSON } from "legal-entities/agreements/_schema/entity-agreement-schema";
import AcceptEntityUserInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/accept-entity-user-invitation-api-command";
import InviteEntityOfficerAPIRequest from "legal-entities/invitations/officer-invitations/_api/commands/invite-entity-officer-api-request";
import EntityOfficerInvitation, {
  EntityOfficerInvitationNotOpenError,
  EntityOfficerInvitationNotFoundError,
  UnauthorizedEntityOfficerInvitationAccessError,
} from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { EntityOfficerInvitationJSON } from "legal-entities/invitations/officer-invitations/_schema/entity-officer-invitation-schema";
import {
  InvitationForAnotherAccountError,
  InvitationLinkInvalidError,
  InvitationRequiresSignInError,
} from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { InvalidRegistrationInfoError } from "legal-entities/registration-info/registration-info";
import { InvitationStatus } from "legal-entities/invitations/statuses/invitation-status";
import Session from "users/__session/session";

type InviteEntityOfficerArgs = {
  invitation: EntityOfficerInvitation;
  operationId: Guid;
};

export enum OfficerSetupOperationStatus {
  Pending = "Pending",
  Completed = "Completed",
  Cancelled = "Cancelled",
  NeedsStaffResolution = "NeedsStaffResolution",
}

export enum OfficerSetupStageStatus {
  Pending = "Pending",
  Started = "Started",
  Completed = "Completed",
  NotApplicable = "NotApplicable",
  Cancelled = "Cancelled",
}

export enum OfficerSetupPendingStage {
  DeliveryUnknown = "DeliveryUnknown",
}

export type OfficerSetupStatus = {
  operationId: string;
  status: OfficerSetupOperationStatus;
  stages: {
    account: OfficerSetupStageStatus;
    permission: OfficerSetupStageStatus;
    marketplace: OfficerSetupStageStatus;
    invitation: OfficerSetupStageStatus;
    delivery: OfficerSetupStageStatus;
  };
  invitationId?: string;
  deliveryResolutionReason?: string | null;
};

type OfficerSetupPendingResponse = {
  operationId: string;
  status: OfficerSetupOperationStatus.Pending;
  stage: OfficerSetupPendingStage.DeliveryUnknown;
  traceId?: string;
};

type OfficerInvitationIdArgs = {
  id: Guid;
};

/**
 * How the invitee shows the invitation is theirs (#6363): signed in as the account it was made for,
 * or holding the token from the emailed link while setting that account's name and password.
 */
export type OfficerInvitationAcceptance = { signedIn: true } | { signedIn: false; token: string };

type AcceptOfficerInvitationArgs = {
  invitation: EntityOfficerInvitation;
  session: Session;
  acceptance: OfficerInvitationAcceptance;
};

type CancelOfficerInvitationArgs = {
  invitation: EntityOfficerInvitation;
};

/** What the open officer invitations can be ordered by, named as the API's `EntityOfficerInvitationOrderField` is. */
export enum EntityOfficerInvitationOrderField {
  DateSent = "DateSent",
}

/** The two statuses an open invitation can have, and so the only two the list filters on. */
export type OpenOfficerInvitationStatus = InvitationStatus.Open | InvitationStatus.Expired;

/**
 * What one page of the open officer invitations is asked for with (#5448).
 *
 * Every field goes to the server. Its defaults are newest first, both statuses, and the first page
 * of 25.
 */
export type OpenOfficerInvitationListQuery = {
  /** Part of the invitee's name or email address, or of the invited organisation's name. */
  search?: string;
  status?: OpenOfficerInvitationStatus;
  orderBy?: EntityOfficerInvitationOrderField;
  orderDir?: OrderDirection;
  pageIndex?: number;
  pageSize?: number;
};

/** The query string for one page of open invitations. A blank search is left off rather than sent. */
export function openOfficerInvitationListParams(query: OpenOfficerInvitationListQuery): URLSearchParams {
  const params = new URLSearchParams();
  const search = query.search?.trim();
  if (search) params.set("search", search);
  if (query.status) params.set("status", query.status);
  if (query.orderBy) params.set("orderBy", query.orderBy);
  if (query.orderDir) params.set("orderDir", query.orderDir);
  if (query.pageIndex !== undefined) params.set("pageIndex", `${query.pageIndex}`);
  if (query.pageSize !== undefined) params.set("pageSize", `${query.pageSize}`);
  return params;
}

// The superuser dashboard's list of still-open entity officer invitations. Sending and
// cancelling both change what belongs on it.
const openOfficerInvitationsTag = { type: "EntityOfficerInvitation" as const, id: "OPEN_LIST" };

const jsonHeaders = {
  "Content-Type": "application/json",
};

export const entityOfficerInvitationApi = api.injectEndpoints({
  endpoints: (build) => ({
    inviteEntityOfficer: build.mutation<EntityOfficerInvitation, InviteEntityOfficerArgs>({
      async queryFn({ invitation, operationId }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: "super-user/invite-entity-officer",
          method: "POST",
          body: JSON.stringify(new InviteEntityOfficerAPIRequest(invitation, operationId)),
          headers: jsonHeaders,
        });

        if (response.error) {
          const status = getApiErrorStatus(response.error);
          if (typeof status === "string") {
            return {
              error: toQueryError(new OfficerSetupPendingError({
                operationId: operationId.value,
                status: OfficerSetupOperationStatus.Pending,
                stage: OfficerSetupPendingStage.DeliveryUnknown,
              })),
            };
          }
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("inviteEntityOfficer", response.error, invitation)),
          };
        }

        if (isOfficerSetupPendingResponse(response.data)) {
          return { error: toQueryError(new OfficerSetupPendingError(response.data)) };
        }

        return {
          data: EntityOfficerInvitation.create({
            params: {
              ...invitation,
              id: new Guid(response.data as string),
            },
          }),
        };
      },
      invalidatesTags: [openOfficerInvitationsTag],
    }),
    getOfficerSetupStatus: build.query<OfficerSetupStatus, Guid>({
      async queryFn(operationId, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/officer-setups/${operationId.value}`);
        if (response.error) {
          if (typeof getApiErrorStatus(response.error) === "string") {
            return { error: toQueryError(new OfficerSetupPendingError({
              operationId: operationId.value,
              status: OfficerSetupOperationStatus.Pending,
              stage: OfficerSetupPendingStage.DeliveryUnknown,
            })) };
          }
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getOfficerSetupStatus", response.error)),
          };
        }
        return { data: response.data as OfficerSetupStatus };
      },
    }),
    getOpenOfficerInvitations: build.query<Paginated<EntityOfficerInvitation>, OpenOfficerInvitationListQuery>({
      async queryFn(query, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: "legal-entities/officer-invitations",
          params: openOfficerInvitationListParams(query),
        });

        if (response.error) {
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getOpenOfficerInvitations", response.error)),
          };
        }

        // The page is the body; every total is in X-Pagination.
        const header = response.meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: {
            data: EntityOfficerInvitation.fromJSONArray(response.data as EntityOfficerInvitationJSON[]),
            headers: PaginatedResponse.parsePaginationHeader(header),
          },
        };
      },
      // Every page shares the one tag, so sending or cancelling refreshes whichever page is showing.
      providesTags: [openOfficerInvitationsTag],
    }),
    getOfficerInvitationById: build.query<EntityOfficerInvitation, OfficerInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery(`legal-entities/officer-invitations/${id.value}`);

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new EntityOfficerInvitationNotFoundError(id)) };
          }

          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("getOfficerInvitationById", response.error)),
          };
        }

        return { data: EntityOfficerInvitation.fromJSON(response.data as EntityOfficerInvitationJSON) };
      },
    }),
    acceptOfficerInvitation: build.mutation<EntityAgreement, AcceptOfficerInvitationArgs>({
      async queryFn({ invitation, session, acceptance }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Invitation ID is required to accept an invitation.")) };
        }
        if (!acceptance.signedIn && !invitation.registrationInfo?.password) {
          return { error: toQueryError(new Error("Password is required to accept an invitation.")) };
        }

        // Signed in, the session's bearer token is the proof and the account keeps its own name and
        // password, so the body is empty. Registering, the token from the emailed link rides with the
        // account's name and password.
        const body = acceptance.signedIn
          ? {}
          : {
              ...new AcceptEntityUserInvitationAPIRequest(
                invitation.registrationInfo.password!,
                invitation.registrationInfo.name,
              ),
              token: acceptance.token,
            };
        const response = await baseQuery({
          url: `legal-entities/officer-invitations/${invitation.id.value}/accept`,
          method: "POST",
          body: JSON.stringify(body),
          headers: jsonHeaders,
        });

        if (response.error) {
          const refusal = readAcceptanceRefusal(response.error);
          if (refusal) return { error: toQueryError(refusal) };
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new EntityOfficerInvitationNotOpenError(conflictMessage(response.error))) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.BadRequest) {
            return { error: toQueryError(new InvalidRegistrationInfoError(getApiErrorData(response.error))) };
          }

          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("acceptOfficerInvitation", response.error)),
          };
        }

        // Accepting an officer invitation is a login context: the response carries the shared auth
        // payload at the top level, alongside the agreement the new officer still has to accept.
        const data = response.data as { entityAgreement: unknown };
        session.initializeFromAuthPayload(response.data);

        return { data: EntityAgreement.fromJSON(data.entityAgreement as EntityAgreementJSON) };
      },
    }),
    resendOfficerInvitationLink: build.mutation<void, OfficerInvitationIdArgs>({
      async queryFn({ id }, _queryApi, _extraOptions, baseQuery) {
        const response = await baseQuery({
          url: `legal-entities/officer-invitations/${id.value}/resend-link`,
          method: "POST",
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new EntityOfficerInvitationNotFoundError(id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new EntityOfficerInvitationNotOpenError(conflictMessage(response.error))) };
          }
          return {
            error: toQueryError(new EntityOfficerInvitationServiceError("resendOfficerInvitationLink", response.error)),
          };
        }

        return { data: null as unknown as void };
      },
    }),
    cancelEntityOfficerInvitation: build.mutation<void, CancelOfficerInvitationArgs>({
      async queryFn({ invitation }, _queryApi, _extraOptions, baseQuery) {
        if (!invitation.id) {
          return { error: toQueryError(new Error("Cannot cancel an invitation that has not been saved.")) };
        }

        const response = await baseQuery({
          url: `legal-entities/officer-invitations/${invitation.id.value}/cancel`,
          method: "POST",
          body: {},
        });

        if (response.error) {
          if (getApiErrorStatus(response.error) === HttpStatusCode.NotFound) {
            return { error: toQueryError(new EntityOfficerInvitationNotFoundError(invitation.id)) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Forbidden) {
            return { error: toQueryError(new UnauthorizedEntityOfficerInvitationAccessError()) };
          }
          if (getApiErrorStatus(response.error) === HttpStatusCode.Conflict) {
            return { error: toQueryError(new EntityOfficerInvitationNotOpenError(conflictMessage(response.error))) };
          }

          return {
            error: toQueryError(
              new EntityOfficerInvitationServiceError("cancelEntityOfficerInvitation", response.error, invitation),
            ),
          };
        }

        return { data: null as unknown as void };
      },
      invalidatesTags: [openOfficerInvitationsTag],
    }),
  }),
});

export class EntityOfficerInvitationServiceError extends Error {
  invitation?: EntityOfficerInvitation;
  method: string;
  error: unknown;

  constructor(method: string, error: unknown, invitation?: EntityOfficerInvitation) {
    super(`Error inviting entity officer: ${error instanceof Error ? error.message : String(error)}`);
    this.method = method;
    this.error = error;
    this.invitation = invitation;
  }
}

export class OfficerSetupPendingError extends Error {
  readonly operationId: Guid;
  readonly traceId?: string;

  constructor(response: OfficerSetupPendingResponse) {
    super("The officer invitation delivery result is still pending.");
    this.operationId = new Guid(response.operationId);
    this.traceId = response.traceId;
  }
}

function isOfficerSetupPendingResponse(data: unknown): data is OfficerSetupPendingResponse {
  if (!data || typeof data !== "object") return false;
  const candidate = data as Partial<OfficerSetupPendingResponse>;
  return candidate.status === OfficerSetupOperationStatus.Pending &&
    candidate.stage === OfficerSetupPendingStage.DeliveryUnknown &&
    typeof candidate.operationId === "string" &&
    (candidate.traceId === undefined || typeof candidate.traceId === "string");
}

function toQueryError(error: Error): FetchBaseQueryError {
  return error as unknown as FetchBaseQueryError;
}

/**
 * The three ways acceptance refuses somebody who has not shown they are the invitee, each of which
 * the page answers differently: a fresh link, a sign-in, or a sign-out.
 */
function readAcceptanceRefusal(error: unknown): Error | undefined {
  const result = readApiError(error);
  if (!result.trusted) return undefined;
  switch (result.code) {
    case "officer_invitation_link_invalid":
      return new InvitationLinkInvalidError();
    case "officer_invitation_requires_sign_in":
      return new InvitationRequiresSignInError();
    case "officer_invitation_for_another_account":
      return new InvitationForAnotherAccountError();
    default:
      return undefined;
  }
}

function conflictMessage(error: unknown): string | undefined {
  const result = readApiError(error);
  return result.trusted ? getApiErrorUserMessage(result) : undefined;
}

export const {
  useInviteEntityOfficerMutation,
  useLazyGetOfficerSetupStatusQuery,
  useGetOpenOfficerInvitationsQuery,
  useLazyGetOfficerInvitationByIdQuery,
  useGetOfficerInvitationByIdQuery,
  useAcceptOfficerInvitationMutation,
  useResendOfficerInvitationLinkMutation,
  useCancelEntityOfficerInvitationMutation,
} = entityOfficerInvitationApi;
